/**
 * Admin Dashboard — Overview of all clients, credits, and agents
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, IS_REHEARSAL } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';
import { AdminTestCallModal } from './AdminTestCallModal';
import { ResendWelcomeEmailModal } from './ResendWelcomeEmailModal';
import { EmailVerificationModal } from './EmailVerificationModal';
import { HelpModelCard } from './HelpModelCard';

interface ClientInfo {
  id: string;
  name: string;
  email: string;
  credits: number;
  // Optional: a listClients deployed before 2026-10-28 sends neither, and every row then reads as
  // per_call - the same view as before.
  billingType?: 'per_call' | 'per_contact';
  contactCredits?: number;
  autoDispatchEnabled: boolean;
  leadCount: number;
  resultCount: number;
  agentName: string | null;
  createdAt: string;
}

// A per_contact client spends contactCredits (dispatcher.ts); everyone else spends credits.
// Same rule as useCredits and Manage Client.
const isPerContact = (c: ClientInfo) => c.billingType === 'per_contact';
const spendableCredits = (c: ClientInfo) => (isPerContact(c) ? c.contactCredits : c.credits) || 0;

export function AdminDashboard() {
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTestCall, setShowTestCall] = useState(false);
  const [showResendWelcome, setShowResendWelcome] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const listClientsFn = httpsCallable(functions, 'listClients');
      const result = await listClientsFn({});
      const data = result.data as { clients: ClientInfo[] };
      setClients(data.clients);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  // Each card adds up its own rows. Call credits left on a per-contact account are never spent,
  // so they are not counted (Manage Credits still shows them).
  const totalCallCredits = clients.filter(c => !isPerContact(c)).reduce((sum, c) => sum + spendableCredits(c), 0);
  const totalContactCredits = clients.filter(isPerContact).reduce((sum, c) => sum + spendableCredits(c), 0);
  const totalLeads = clients.reduce((sum, c) => sum + c.leadCount, 0);
  const totalResults = clients.reduce((sum, c) => sum + c.resultCount, 0);

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container">
        {/* Summary Stats */}
        <div className="stats-grid">
          <div className="stat-card green">
            <h3>Total Clients</h3>
            <span className="count">{clients.length}</span>
          </div>
          <div className="stat-card blue">
            <h3>Total Call Credits</h3>
            <span className="count">{totalCallCredits.toLocaleString()}</span>
          </div>
          <div className="stat-card blue">
            <h3>Total Contact Credits</h3>
            <span className="count">{totalContactCredits.toLocaleString()}</span>
          </div>
          <div className="stat-card orange">
            <h3>Total Leads</h3>
            <span className="count">{totalLeads.toLocaleString()}</span>
          </div>
          <div className="stat-card red">
            <h3>Total Results</h3>
            <span className="count">{totalResults.toLocaleString()}</span>
          </div>
        </div>

        {/* Quick Actions */}
        <h2 className="section-title">Quick Actions</h2>
        <div className="actions" style={{ marginBottom: '32px' }}>
          {/* Hidden on the rehearsal bench: these email people, create Google
              Sheets or call Retell even when the portal is on the emulators.
              Bench users come from the seed script only. */}
          {!IS_REHEARSAL && <Link to="/admin/create-client" className="btn btn-primary">Create Client</Link>}
          {!IS_REHEARSAL && <Link to="/admin/create-agent" className="btn btn-primary">Create Agent</Link>}
          <Link to="/admin/verification-jobs" className="btn btn-secondary">Verification Jobs</Link>
          <Link to="/admin/imdata" className="btn btn-secondary">IMData Upload (free)</Link>
          <Link to="/admin/tickets" className="btn btn-primary">Support Tickets</Link>
          <Link to="/admin/promo-codes" className="btn btn-secondary">Promo Codes</Link>
          {!IS_REHEARSAL && <button onClick={() => setShowTestCall(true)} className="btn btn-primary"
            style={{ background: 'linear-gradient(90deg, #b8860b, #ddb154)', color: '#000', fontWeight: 600 }}>
            Send Demo Call
          </button>}
          {!IS_REHEARSAL && <button onClick={() => setShowResendWelcome(true)} className="btn btn-secondary">
            Resend Welcome Email
          </button>}
          {!IS_REHEARSAL && <button onClick={() => setShowEmailVerification(true)} className="btn btn-secondary">
            Email Verification
          </button>}
          <button onClick={fetchClients} className="btn btn-secondary" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <AdminTestCallModal isOpen={showTestCall} onClose={() => setShowTestCall(false)} />
        <ResendWelcomeEmailModal
          isOpen={showResendWelcome}
          onClose={() => setShowResendWelcome(false)}
          clients={clients.map(c => ({ id: c.id, name: c.name, email: c.email }))}
        />
        <EmailVerificationModal
          isOpen={showEmailVerification}
          onClose={() => setShowEmailVerification(false)}
          clients={clients.map(c => ({ id: c.id, name: c.name, email: c.email }))}
        />

        <HelpModelCard />

        {/* Clients Table */}
        <h2 className="section-title">All Clients</h2>
        {error && <div className="error-message">{error}</div>}
        {loading ? (
          <div className="loading">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="loading">No clients yet. Create one to get started.</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Credits</th>
                  <th>Leads</th>
                  <th>Results</th>
                  <th>Agent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td style={{ fontWeight: 600 }}>{client.name}</td>
                    <td>{client.email}</td>
                    <td>
                      <span style={{ color: spendableCredits(client) > 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                        {spendableCredits(client).toLocaleString()}
                      </span>
                      <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {isPerContact(client) ? 'Contact' : 'Call'}
                      </span>
                    </td>
                    <td>{client.leadCount.toLocaleString()}</td>
                    <td>{client.resultCount.toLocaleString()}</td>
                    <td style={{ fontSize: '0.85rem', color: client.agentName ? 'var(--gray-200)' : 'var(--gray-500)' }}>
                      {client.agentName || 'None'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Link to={`/admin/client/${client.id}`} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                          Manage
                        </Link>
                        {!IS_REHEARSAL && <Link to={`/admin/credits/${client.id}`} className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                          Credits
                        </Link>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
